
var LoadTexture	= require('../../../utils/loadTexture')
var Random = require('../../../utils/random')

var internals = {
	
	createMaterial : function( color, range ) {
		
		var material = new THREE.ShaderMaterial({
		
			vertexShader    : "#define GLSLIFY 1\n\nuniform float elapsed;\nuniform float uRange;\nattribute float size;\nattribute float aOffset;\nvarying float vAlpha;\n\n//\n// Description : Array and textureless GLSL 2D/3D/4D simplex\n//               noise functions.\n//      Author : Ian McEwan, Ashima Arts.\n//  Maintainer : ijm\n//     Lastmod : 20110822 (ijm)\n//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.\n//               Distributed under the MIT License. See LICENSE file.\n//               https://github.com/ashima/webgl-noise\n//\n\nvec3 mod289_1_0(vec3 x) {\n  return x - floor(x * (1.0 / 289.0)) * 289.0;\n}\n\nvec4 mod289_1_0(vec4 x) {\n  return x - floor(x * (1.0 / 289.0)) * 289.0;\n}\n\nvec4 permute_1_1(vec4 x) {\n     return mod289_1_0(((x*34.0)+1.0)*x);\n}\n\nvec4 taylorInvSqrt_1_2(vec4 r)\n{\n  return 1.79284291400159 - 0.85373472095314 * r;\n}\n\nfloat snoise_1_3(vec3 v)\n  {\n  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;\n  const vec4  D_1_4 = vec4(0.0, 0.5, 1.0, 2.0);\n\n// First corner\n  vec3 i  = floor(v + dot(v, C.yyy) );\n  vec3 x0 =   v - i + dot(i, C.xxx) ;\n\n// Other corners\n  vec3 g_1_5 = step(x0.yzx, x0.xyz);\n  vec3 l = 1.0 - g_1_5;\n  vec3 i1 = min( g_1_5.xyz, l.zxy );\n  vec3 i2 = max( g_1_5.xyz, l.zxy );\n\n  //   x0 = x0 - 0.0 + 0.0 * C.xxx;\n  //   x1 = x0 - i1  + 1.0 * C.xxx;\n  //   x2 = x0 - i2  + 2.0 * C.xxx;\n  //   x3 = x0 - 1.0 + 3.0 * C.xxx;\n  vec3 x1 = x0 - i1 + C.xxx;\n  vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y\n  vec3 x3 = x0 - D_1_4.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y\n\n// Permutations\n  i = mod289_1_0(i);\n  vec4 p = permute_1_1( permute_1_1( permute_1_1(\n             i.z + vec4(0.0, i1.z, i2.z, 1.0 ))\n           + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))\n           + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));\n\n// Gradients: 7x7 points over a square, mapped onto an octahedron.\n// The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)\n  float n_ = 0.142857142857; // 1.0/7.0\n  vec3  ns = n_ * D_1_4.wyz - D_1_4.xzx;\n\n  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)\n\n  vec4 x_ = floor(j * ns.z);\n  vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)\n\n  vec4 x = x_ *ns.x + ns.yyyy;\n  vec4 y = y_ *ns.x + ns.yyyy;\n  vec4 h = 1.0 - abs(x) - abs(y);\n\n  vec4 b0 = vec4( x.xy, y.xy );\n  vec4 b1 = vec4( x.zw, y.zw );\n\n  //vec4 s0 = vec4(lessThan(b0,0.0))*2.0 - 1.0;\n  //vec4 s1 = vec4(lessThan(b1,0.0))*2.0 - 1.0;\n  vec4 s0 = floor(b0)*2.0 + 1.0;\n  vec4 s1 = floor(b1)*2.0 + 1.0;\n  vec4 sh = -step(h, vec4(0.0));\n\n  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;\n  vec4 a1_1_6 = b1.xzyw + s1.xzyw*sh.zzww ;\n\n  vec3 p0_1_7 = vec3(a0.xy,h.x);\n  vec3 p1 = vec3(a0.zw,h.y);\n  vec3 p2 = vec3(a1_1_6.xy,h.z);\n  vec3 p3 = vec3(a1_1_6.zw,h.w);\n\n//Normalise gradients\n  vec4 norm = taylorInvSqrt_1_2(vec4(dot(p0_1_7,p0_1_7), dot(p1,p1), dot(p2, p2), dot(p3,p3)));\n  p0_1_7 *= norm.x;\n  p1 *= norm.y;\n  p2 *= norm.z;\n  p3 *= norm.w;\n\n// Mix final noise value\n  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);\n  m = m * m;\n  return 42.0 * dot( m*m, vec4( dot(p0_1_7,x0), dot(p1,x1),\n                                dot(p2,x2), dot(p3,x3) ) );\n  }\n\n\n\n\nvoid main() {\n\n\t// vec3 movement = vec3(0.0, elapsed * 0.001, 0.0);\n\tvec3 movement = vec3(\n\t\tsin( elapsed * 0.002 * aOffset) * uRange * 0.003,\n\t\telapsed * (-uRange / 100000.0 - uRange / 100000.0 * aOffset),\n\t\tcos( elapsed * 0.002 * aOffset ) * uRange * 0.003\n\t);\n\t\n\tvec3 range = vec3(uRange, uRange * 0.3, uRange);\n\t\n\tvec3 cameraOffset = cameraPosition - range;\n\t\n\tvec3 moduloPosition = mod( position + movement - cameraOffset, range * 2.0 ) + cameraOffset;\n\tvec4 mvPosition = modelViewMatrix * vec4( moduloPosition, 1.0 );\n\t\n\tgl_PointSize = size * ( uRange / (length( mvPosition.xyz ) + 1.0) );\n\t\n\tfloat flicker = mix(0.6, 1.0, snoise_1_3( moduloPosition + elapsed * 0.0001 ));\n\t\n\tvAlpha = 0.5 * min(1.0, max(0.0,\n\t\t1.0 - (length( mvPosition.xyz ) / uRange)\n\t)) * flicker;\n\n\tgl_Position = projectionMatrix * mvPosition;\n\n}",
			fragmentShader  : "#define GLSLIFY 1\n\nuniform vec3 color;\nuniform sampler2D texture;\nuniform float elapsed;\n\nvarying float vAlpha;\n\nvoid main() {\n\n\tgl_FragColor = vec4(color, vAlpha) * texture2D( texture, gl_PointCoord );\n\t\n}",
			
			blending:       THREE.AdditiveBlending,
			depthTest:      false,
			transparent:    true,
		
			uniforms: {
				elapsed : { type: 'f' },
				texture : { type: 't' },
				color   : { type: "c", value: color },
				uRange  : { type: "f", value: range },
			},
			attributes      : {
				position: { type: 'v3' },
				size: { type: 'f' },
				aOffset: { type: 'f' },
			}
		})
		
		return material
	},
	
	createGeometry : function( material, count, range, sizeRange ) {
		
		var geometry = new THREE.BufferGeometry()

		var positions = new Float32Array( count * 3 )
		var sizes = new Float32Array( count )
		var offsets = new Float32Array( count )

		for( var i = 0; i < count; i++ ) {
			
			positions[ i * 3 + 0 ] = Random.range( -range, range )
			positions[ i * 3 + 1 ] = Random.range( -range, range ) * 0.3
			positions[ i * 3 + 2 ] = Random.range( -range, range )

			sizes[ i ] = Random.range( sizeRange[0], sizeRange[1] )
			offsets[ i ] = Random.range( 0, 1 )

		}
		
		geometry.addAttribute( 'position', new THREE.BufferAttribute( positions, 3 ) )
		geometry.addAttribute( 'size',     new THREE.BufferAttribute( sizes, 1 ) )
		geometry.addAttribute( 'aOffset',  new THREE.BufferAttribute( offsets, 1 ) )
		
		return geometry
	},
	
	createMeshAndStart : function( poem, geometry, material ) {
		
		var mesh = new THREE.PointCloud( geometry, material )
		mesh.frustumCulled = false

		var p = LoadTexture( "assets/images/bokeh.png", material.uniforms.texture, "value" ).then(function() {

			poem.scene.add( mesh )
			poem.emitter.on('update', internals.updateFn( poem, mesh ))
		})
		
		return mesh
	},
	
	updateFn : function( poem, mesh ) {
		
		return function(e) {
			
			// mesh.position.copy( poem.camera.object.position )
			mesh.material.uniforms.elapsed.value = e.elapsed;
		}
	}
}


module.exports = function particles( poem, properties ) {

	var config = _.extend({
		count:		3000
	  , color:		new THREE.Color(0x77ffff)
	  , range:		300
	  , sizeRange:	[3,8]
	}, properties)
	
	var material = internals.createMaterial(
		config.color
	  , config.range
	)
	
	var geometry = internals.createGeometry(
		material
	  , config.count
	  , config.range
	  , config.sizeRange
	)
	
	var mesh = internals.createMeshAndStart(
		poem,
		geometry,
		material
	)
	
	return {}
}